import React from "react";
import { Menu, X } from "lucide-react";
import Wordmark from "../../components/public/Wordmark";

/**
 * Shell composition:
 *   <AppShell theme="erf-operator" navItems={...} topBar={<TopBar .../>}>
 *     …portal body…
 *   </AppShell>
 *
 * R1b-α Fix #3 additions:
 * - `mobileNavOpen` + `onToggleMobileNav` for a responsive collapse (<768px).
 * - `role="navigation"` + aria-label on the side rail.
 * - Keyboard-activatable nav items (Enter / Space) with visible focus rings
 *   provided by base.css.
 * - `prefers-reduced-motion` honoured by CSS transitions (opacity only).
 *
 * @param {Object} props
 * @param {"erf-operator"|"erf-public"|"platform"} [props.theme="erf-operator"]
 * @param {Array<{key:string,label:string,icon?:React.ReactNode,badge?:string,active?:boolean,onClick?:()=>void}>} [props.navItems]
 * @param {React.ReactNode} [props.topBar]
 * @param {React.ReactNode} [props.brand] — brand block (mark + name).
 * @param {boolean} [props.mobileNavOpen] — controls the responsive drawer.
 * @param {()=>void} [props.onToggleMobileNav]
 */
export function AppShell({
  theme = "erf-operator",
  navItems = [],
  topBar,
  brand,
  children,
  mobileNavOpen = false,
  onToggleMobileNav,
}) {
  return (
    <div className={`ds-scope ds-theme-${theme}`}>
      <div className={`ds-shell ${mobileNavOpen ? "is-mobile-nav-open" : ""}`.trim()}>
        <aside
          className="ds-shell-side"
          role="navigation"
          aria-label="Primary navigation"
          data-testid="admin-shell-nav"
        >
          <div className="ds-shell-side-header">
            {brand ?? <DefaultBrand theme={theme} />}
            {onToggleMobileNav && (
              <button
                type="button"
                className="ds-shell-mobile-close"
                aria-label="Close navigation"
                onClick={onToggleMobileNav}
                data-testid="admin-shell-mobile-nav-close"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {navItems.map((n) => (
            <div
              key={n.key}
              className={`ds-shell-item ${n.active ? "is-active" : ""}`.trim()}
              onClick={n.onClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  n.onClick?.();
                }
              }}
              role="button"
              tabIndex={0}
              aria-current={n.active ? "page" : undefined}
              data-testid={`admin-shell-nav-item-${n.key}`}
            >
              {n.icon}{n.label}
              {n.badge && <span className="badge">{n.badge}</span>}
            </div>
          ))}
        </aside>
        <div className="ds-shell-main-column">
          <div className="ds-shell-topbar-row">
            {onToggleMobileNav && (
              <button
                type="button"
                className="ds-shell-mobile-toggle"
                aria-label="Open navigation"
                aria-expanded={mobileNavOpen}
                aria-controls="admin-shell-nav"
                onClick={onToggleMobileNav}
                data-testid="admin-shell-mobile-nav-toggle"
              >
                <Menu size={18} />
              </button>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>{topBar}</div>
          </div>
          <div className="ds-shell-body">{children}</div>
        </div>
        {mobileNavOpen && onToggleMobileNav && (
          <div
            className="ds-shell-mobile-backdrop"
            onClick={onToggleMobileNav}
            aria-hidden="true"
            data-testid="admin-shell-mobile-backdrop"
          />
        )}
      </div>
    </div>
  );
}

function DefaultBrand({ theme }) {
  // R1-VISUAL-INWARD: operator chrome now carries the same v2 logo mark
  // rendered on public surfaces. The reversed asset reads on the deep-navy
  // operator canvas. Platform theme keeps the text label as before.
  if (theme === "erf-operator") {
    return (
      <div className="ds-shell-brand">
        <Wordmark tone="light" size="sm" />
      </div>
    );
  }
  return (
    <div className="ds-shell-brand">
      <div className="ds-shell-brand-mark" />
      <div className="ds-shell-brand-name">
        {theme === "platform" ? "Construction OS" : "ERF"}
      </div>
    </div>
  );
}
